import { useCallback, useContext, useEffect, useRef, useState } from "react"

//Activity tracker
import {
  activityTrackingSettingNames,
  getSettingOverrideValue,
} from "../../components/DeveloperTools/ActivityTracker/activitySettingsHelpers"

//Context
import { FirebaseContext } from "../../firebase"

//helpers
import { getEnvironmentCode } from "../../components/DeveloperTools/developerToolsHelpers"
import { LogError } from "../../helpers/logger"
import useActivityConfig from "./useActivityConfig"

export const ACTIVITY_STATES = {
  ACTIVE: "active",
  OFFLINE: "offline",
}

/**
 * This uses BroadcastChannel for modern browsers to sync activity state across tabs, and
 * falls back to localStorage events for older browsers.
 * It tracks user activity based on common events and updates the state accordingly.
 * @param {string} channelName
 * @param {function} onMessage
 * @returns {object} An object with `post` and `close` methods for the channel.
 */
function createCrossTabChannel(channelName, onMessage) {
  // Modern browsers → BroadcastChannel
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(channelName)
    channel.onmessage = (msg) => onMessage(msg.data)

    return {
      post: (data) => channel.postMessage(data),
      close: () => channel.close(),
    }
  }

  // Fallback → localStorage events
  const key = `__${channelName}__`

  const handler = (e) => {
    if (e.key === key && e.newValue) {
      try {
        onMessage(JSON.parse(e.newValue))
      } catch (error) {
        LogError(
          "createCrossTabChannel: Failed to parse cross-tab message",
          error,
        )
      }
    }
  }

  window.addEventListener("storage", handler)

  return {
    post: (data) => {
      localStorage.setItem(key, JSON.stringify(data))
      // Clean up so storage events fire again next time
      localStorage.removeItem(key)
    },
    close: () => {
      window.removeEventListener("storage", handler)
    },
  }
}


const channelName = "rl_activity_tracker"
const BROADCAST_THROTTLE_MS = 30 * 1000 // 30 seconds
const ACTIVE_STATUS_CHECK_INTERVAL_MS = 5000 // 5 seconds
const INACTIVE_STATUS_CHECK_INTERVAL_MS = 1000 // 1 second

export function useActivityTracker({ doMonitor, onWarning, mainTokenReady = true }) {
  const firebase = useContext(FirebaseContext)
  const { getLogoutAfter, getWarningDuration, loadingConfig} = useActivityConfig({
    enabled: mainTokenReady,
  })

  const [refreshToggle, setRefreshToggle] = useState(false)

  // State is tracked in addition to the refs to trigger re-renders
  const [state, setState] = useState(ACTIVITY_STATES.ACTIVE)
  const [countdown, setCountdown] = useState(null)

  // keep refs aligned to state, these are used in the interval and broadcast handlers to ensure they have access to the latest
  // values without needing to add them to the dependency arrays
  const lastActiveRef = useRef(Date.now())
  const stateRef = useRef(state)
  const countdownRef = useRef(countdown)
  const logoutAfterRef = useRef(getLogoutAfter())
  const warningDurationRef = useRef(getWarningDuration())
  const lastBroadcastRef = useRef(0)
  const crossTabChannelRef = useRef(null)
  const logoutInProgressRef = useRef(false)

  const doDisableAutoLogout =
    process.env.REACT_APP_DISABLE_AUTO_LOGOUT === "TRUE"

  // Load settings overrides on mount and when refreshToggle changes (triggered by refreshSettings function)
  useEffect(() => {
    if(loadingConfig) return

    // Check for setting overrides in localStorage (set by the Developer Tools) and apply them if they exist, otherwise use the values from the config
    const envCode = getEnvironmentCode()
    const storedLogoutAfterOverride = getSettingOverrideValue(
      `${activityTrackingSettingNames.logoutTimeout}${envCode}`,
    )
    const storedWarningDurationOverride = getSettingOverrideValue(
      `${activityTrackingSettingNames.warningDuration}${envCode}`,
    )

    const configLogoutAfter = getLogoutAfter()
    // If there is an override value, use it. Otherwise, ensure that the ref values are in sync with the config values
    if (storedLogoutAfterOverride?.overrideValue) {
      const newValue = storedLogoutAfterOverride.overrideValue * 60 * 1000
      setLogOutAfterValue(newValue)
    } else if (logoutAfterRef.current !== configLogoutAfter) {
      setLogOutAfterValue(configLogoutAfter)
    }

    const configWarningDuration = getWarningDuration()
    // If there is an override value, use it. Otherwise, ensure that the ref values are in sync with the config values
    if (storedWarningDurationOverride?.overrideValue) {
      setWarningDurationValue(
        storedWarningDurationOverride.overrideValue * 1000,
      ) // Convert seconds to ms
    } else if (warningDurationRef.current !== configWarningDuration) {
      setWarningDurationValue(configWarningDuration)
    }
  }, [refreshToggle, loadingConfig, getLogoutAfter, getWarningDuration])

  const refreshSettings = () => setRefreshToggle((prev) => !prev)

  // Resets the activity timer and broadcasts the update to other tabs
  const resetTimer = useCallback((skipResetNotification) => {
    const now = Date.now()
    setLastActiveValue(now)

    setCountdownValue(null)

    setStateValue(ACTIVITY_STATES.ACTIVE)

    // Broadcast to other tabs
    broadcast(now, !skipResetNotification)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Logs out the user and broadcasts the logout event to other tabs
  const logout = useCallback(() => {
    if (logoutInProgressRef.current) return
    logoutInProgressRef.current = true

    if (!doDisableAutoLogout) {
      // Broadcast to other tabs
      const channel = getSharedCrossTabChannel()

      //Notify other tabs of impending logout to prevent multiple signout
      channel.post({ logoutStarted: true })

      firebase.signOut()

      //Notify other tabs to reset their timers for later logins
      channel.post({ resetTimer: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doDisableAutoLogout, firebase])

  // Clean up BroadcastChannel on unmount
  useEffect(() => {
    return () => {
      if (crossTabChannelRef.current) {
        crossTabChannelRef.current.close()
        crossTabChannelRef.current = null
      }
    }
  }, [])

  // Main effect to track activity and manage state
  useEffect(() => {
    if (!doMonitor || loadingConfig) {
      return
    }

    resetTimer() // Initialize lastActive and broadcast to other tabs on mount

    const updateActivity = (e) => {
      // Do not update activity if already offline, at this stage the user needs to explicitly interact with the
      // warning modal to reset the timer and come back online
      if (stateRef.current === ACTIVITY_STATES.OFFLINE) {
        return
      }

      const now = Date.now()
      const isNoisyEvent = e.type === "mousemove" || e.type === "scroll"

      //Reset the countdown if the user becomes active again
      if (countdownRef.current !== null) {
        setCountdownValue(null)
      }

      // Always update the ref so the interval knows we are active
      lastActiveRef.current = now

      //ensure that we have an active state as soon as possible.
      if (stateRef.current !== ACTIVITY_STATES.ACTIVE) {
        setStateValue(ACTIVITY_STATES.ACTIVE)
      }

      // Throttle noisy events
      if (isNoisyEvent) {
        //Use the reference value, or fallback to the possibly stale state value if the ref hasn't been updated yet
        const logoutAfterValue = logoutAfterRef.current || getLogoutAfter()

        const throttleLimit =
          BROADCAST_THROTTLE_MS < logoutAfterValue
            ? BROADCAST_THROTTLE_MS
            : logoutAfterValue / 2

        if (now - (lastBroadcastRef.current || 0) > throttleLimit) {
          setLastActiveValue(now)
          setCountdownValue(null)
          setStateValue(ACTIVITY_STATES.ACTIVE)
          broadcast(now)
        }
        return
      }

      // Non-noisy events → update states & broadcast immediately
      setLastActiveValue(now)
      setCountdownValue(null)
      setStateValue(ACTIVITY_STATES.ACTIVE)
      broadcast(now)
    }

    const activityEvents = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ]
    activityEvents.forEach((e) => window.addEventListener(e, updateActivity))

    let timeoutId

    // Check activity status on an interval to handle tab inactivity and countdown updates
    const checkStatus = (isTick) => {
      if (doDisableAutoLogout || logoutInProgressRef.current) return

      const now = Date.now()
      const inactiveFor = now - lastActiveRef.current

      const currentState = stateRef.current
      const currentCountdown = countdownRef.current
      const overThreshold = inactiveFor - logoutAfterRef.current

      //If we are over the threshold for logout + 5s delay, we should log out immediately
      if (overThreshold > warningDurationRef.current + ACTIVE_STATUS_CHECK_INTERVAL_MS) {
        //ensure that the countDown is null and then logout
        setCountdownValue(null)
        logout()
        return
      }

      if (currentState !== ACTIVITY_STATES.OFFLINE) {
        if (overThreshold > 0) {
          setStateValue(ACTIVITY_STATES.OFFLINE)

          if (currentCountdown === null) {
            const seconds = Math.floor(warningDurationRef.current / 1000)
            setCountdownValue(seconds)
            onWarning?.()
          }
        } else {
          setStateValue(ACTIVITY_STATES.ACTIVE)
          //Reset the countdown if the user becomes active again
          if (currentCountdown !== null) {
            setCountdownValue(null)
          }
        }
      } else {
        if (currentCountdown === 0) {
          //Reset countdown to prevent multiple logout calls
          setCountdownValue(null)
          logout()
        } else if (currentCountdown === null && !logoutInProgressRef.current) {
          const seconds = Math.floor(warningDurationRef.current / 1000)
          setCountdownValue(seconds)
          onWarning?.()
        }
      }

      if (isTick === true) {
        if (        
          currentCountdown !== null &&
          currentCountdown > 0
        ) {
          const next = currentCountdown - 1
          setCountdownValue(next)
        }

        // Determine the next interval
        const nextInterval = countdownRef.current !== null ? INACTIVE_STATUS_CHECK_INTERVAL_MS : ACTIVE_STATUS_CHECK_INTERVAL_MS // 1s during countdown, 5s otherwise
        timeoutId = setTimeout(() => checkStatus(true), nextInterval)
      }

    }

    const tabEvents = ["visibilitychange", "focus"]
    tabEvents.forEach((e) => window.addEventListener(e, checkStatus))

    // Run an initial check immediately to start the process (Dynamic timeouts will handle the rest)
    checkStatus(true) 

    return () => {
      activityEvents.forEach((e) =>
        window.removeEventListener(e, updateActivity),
      )
      tabEvents.forEach((e) => window.removeEventListener(e, checkStatus))
      clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doMonitor, loadingConfig])

  function setLastActiveValue(now) {
    lastActiveRef.current = now
  }

  function setCountdownValue(value) {
    countdownRef.current = value
    setCountdown(value)
  }

  function setStateValue(value) {
    stateRef.current = value
    setState(value)
  }

  function setLogOutAfterValue(value) {
    logoutAfterRef.current = value
  }

  function setWarningDurationValue(value) {
    warningDurationRef.current = value
  }

  // Initializes the cross-tab communication channel if it doesn't exist and returns it
  function getSharedCrossTabChannel() {
    if (!crossTabChannelRef.current) {
      crossTabChannelRef.current = createCrossTabChannel(
        channelName,
        (data) => {
          if (!data) return

          // --- CASE 1: Another tab broadcasted lastActive ---
          if (data.lastActive) {
            const wasOffline = stateRef.current === ACTIVITY_STATES.OFFLINE

            if (!wasOffline) {
              setLastActiveValue(data.lastActive)
              setCountdownValue(null)
            } else if (data.resetTimer) {
              // If this tab is offline but another tab reset → sync immediately
              resetTimer(true)
            }

            return
          }

          // --- CASE 2: Another tab started logout ---
          if (data.logoutStarted && countdownRef.current !== null) {
            setCountdownValue(10) // Set a short countdown to logout to allow the logout process to complete in the other tab before this one logs out
            //Ensure that we have an offline status here. (we should have, but just in case)
            if (stateRef.current !== ACTIVITY_STATES.OFFLINE) {
              setStateValue(ACTIVITY_STATES.OFFLINE)
            }
          }
        },
      )
    }

    return crossTabChannelRef.current
  }

  // Broadcasts the last active time to other tabs, with an option to skip the reset notification to prevent loops
  function broadcast(lastActiveTime, resetTimer = true) {
    const channel = getSharedCrossTabChannel()

    channel.post({
      lastActive: lastActiveTime || Date.now(),
      resetTimer: resetTimer,
    })
    lastBroadcastRef.current = Date.now()
  }

  return {
    state,
    countdown,
    getLogoutAfter: useCallback(() => logoutAfterRef.current, []),
    getWarningDuration: useCallback(() => warningDurationRef.current, []),
    getLastActive: useCallback(() => lastActiveRef.current, []),
    resetTimer,
    logout,
    refreshSettings,
  }
}
